/**
 * Menu System - 設定画面とメニュー制御の統合管理
 * start-button.jsから設定関連処理を分離
 */

import { GameStatus } from '../../core/constants.js';
import gameState from '../../core/game-state.js';
import { pauseGame, resumeGame } from '../../gameplay/game-controls.js';
import { visibilityManager } from '../../platform/visibility-manager.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { canInteract } from '../../shared/utils/game-state-utils.js';
import { DOM_CONSTANTS } from '../constants.js';
import { updateAllButtons } from '../controllers/button-controller.js';
import { SettingsRenderers, DOMHelper } from '../screens/settings-screen.js';
import { notifyMenuOpened, shouldBlockMenuOpen } from '../tutorial/tutorial-guards.js';

/**
 * ナビゲーションスタック管理
 */
class NavigationStack {
  constructor() {
    this.stack = [];
  }

  push(view, payload = null) {
    const entry = typeof view === 'string' ? { view, payload } : view;
    this.stack.push(entry);
    return entry;
  }

  pop() {
    return this.stack.pop();
  }

  current() {
    return this.stack[this.stack.length - 1] || null;
  }

  clear() {
    this.stack.length = 0;
  }

  get length() {
    return this.stack.length;
  }
}

/**
 * メニューシステムのメインクラス
 */
export class MenuSystem {
  constructor() {
    this.navigationStack = new NavigationStack();
    this.isInitialized = false;
  }

  /**
   * メニューシステムの初期化
   */
  initialize() {
    if (this.isInitialized) return;

    this.setupMenuButton();
    this.setupTileButtons();
    this.setupDelegatedHandlers();

    this.isInitialized = true;

    ensurePendingBackgroundMenuOpen();
  }

  /**
   * UI操作可能性の判定
   * @returns {Object} UI状態フラグ
   */
  getUIFlags() {
    const status = gameState.getState(STATE_PATHS.CORE_STATUS);
    const uiState = gameState.getState(STATE_PATHS.UI);
    return {
      isTitle: status === GameStatus.Title,
      canPlayInteract: canInteract(gameState),
      isTargeting: uiState.isSkillTargeting,
      hasAmmo: uiState.remainingSkillUses > 0,
    };
  }

  /**
   * メニューが有効かどうかを判定
   * @param {Object} flags - UI状態フラグ
   * @returns {boolean} メニューが有効かどうか
   */
  isMenuEnabled(flags) {
    return flags.isTitle || (flags.canPlayInteract && !flags.isTargeting);
  }

  /**
   * 設定画面を開く
   */
  openSettings() {
    if (shouldBlockMenuOpen()) {
      return false;
    }

    const board = document.getElementById('board');
    if (!board) return false;

    const isOpen = board.classList.contains(DOM_CONSTANTS.DRAWER_OPEN_CLASS);
    if (!isOpen) {
      board.classList.add(DOM_CONSTANTS.DRAWER_OPEN_CLASS);
      document.documentElement.classList.add(DOM_CONSTANTS.DRAWER_OPEN_CLASS);

      // GameState を更新
      gameState.setState(STATE_PATHS.UI_IS_DRAWER_OPEN, true);

      // ナビゲーションをホームにリセット
      this.navigationStack.clear();
      this.showSettingsHome();

      // ゲームを一時停止
      pauseGame();

      // ボタン状態を明示的に更新
      updateAllButtons(gameState);
      notifyMenuOpened();
    }

    return board.classList.contains(DOM_CONSTANTS.DRAWER_OPEN_CLASS);
  }

  /**
   * 設定ビューに移動
   * @param {string} view - 移動先のビュー名
   * @param {Object} options - オプション
   * @param {boolean} options.push - ナビゲーションスタックにpushするか（デフォルト: true）
   */
  navigateTo(view, options = {}) {
    const { push = true, payload = null } = options;

    let entry = null;
    if (push) {
      entry = this.navigationStack.push(view, payload);
    } else {
      entry = this.navigationStack.current();
    }

    // レンダラーを統合して実際の画面を表示
    const renderer = SettingsRenderers[view];
    if (renderer) {
      if (renderer.title != null) {
        this.setSettingsTitle(renderer.title);
      }
      // 最小インターフェースを渡す（必要になったら段階的に拡張）
      const ctx = {
        closeSettings: this.closeSettings.bind(this),
        menuSystem: this,
        payload: entry ? entry.payload : null,
      };
      renderer.render(ctx);
    } else {
      this.setSettingsTitle('Menu');
      this.showSettingsHome();
    }
  }

  /**
   * 前のビューに戻る
   */
  goBack() {
    const currentEntry = this.navigationStack.current();
    if (currentEntry && currentEntry.view === 'confirm') {
      this.handleConfirmResult(false, { skipNavigate: false });
      return;
    }

    if (this.navigationStack.length === 0) {
      this.closeSettings();
      return;
    }

    this.navigationStack.pop();
    const prevEntry = this.navigationStack.current();

    if (!prevEntry) {
      this.showSettingsHome();
    } else {
      // 前のビューを再表示（pushしない）
      this.navigateTo(prevEntry.view, { push: false });
    }
  }

  handleConfirmResult(result, { skipNavigate = false } = {}) {
    const currentEntry = this.navigationStack.current();
    if (!currentEntry || currentEntry.view !== 'confirm') {
      return;
    }

    const payload = currentEntry.payload || {};

    if (!payload.__resolved && typeof payload.resolve === 'function') {
      payload.__resolved = true;
      payload.resolve(result);
    }

    this.navigationStack.pop();

    if (skipNavigate) return;

    const shouldRestore = result
      ? payload.restoreOnConfirm !== false
      : payload.restoreOnCancel !== false;

    if (!shouldRestore) {
      return;
    }

    const prevEntry = this.navigationStack.current();
    if (!prevEntry) {
      this.showSettingsHome();
    } else {
      this.navigateTo(prevEntry.view, { push: false });
    }
  }

  showConfirmDialog({
    message,
    confirmLabel = 'OK',
    restoreOnConfirm = true,
    restoreOnCancel = true,
  } = {}) {
    return new Promise((resolve) => {
      this.navigateTo('confirm', {
        payload: {
          message,
          confirmLabel,
          restoreOnConfirm,
          restoreOnCancel,
          resolve,
          __resolved: false,
        },
      });
    });
  }

  /**
   * 設定画面を閉じる
   */
  closeSettings() {
    const board = document.getElementById('board');
    if (board) board.classList.remove(DOM_CONSTANTS.DRAWER_OPEN_CLASS);
    document.documentElement.classList.remove(DOM_CONSTANTS.DRAWER_OPEN_CLASS);

    const currentEntry = this.navigationStack.current();
    if (currentEntry && currentEntry.view === 'confirm') {
      this.handleConfirmResult(false, { skipNavigate: true });
    }

    this.navigationStack.clear();

    // GameState を更新
    gameState.setState(STATE_PATHS.UI_IS_DRAWER_OPEN, false);

    resumeGame();

    // ボタン状態を明示的に更新
    updateAllButtons(gameState);
  }

  /**
   * 設定ホーム画面を表示
   */
  showSettingsHome() {
    const { actions } = DOMHelper.getSettingsRefs();
    this.clearSettingsView();
    this.setSettingsTitle('Menu');
    if (actions) actions.style.display = '';
  }

  // ========================================
  // DOM操作関連のヘルパーメソッド
  // ========================================

  /**
   * 設定画面のタイトルを設定
   * @param {string} text - タイトルテキスト
   */
  setSettingsTitle(text) {
    const { titleEl } = DOMHelper.getSettingsRefs();
    if (titleEl) titleEl.textContent = text;
  }

  /**
   * 設定ビューをクリア
   */
  clearSettingsView() {
    DOMHelper.clearSettingsView();
  }

  // ========================================
  // イベントハンドラー設定
  // ========================================

  /**
   * メニューボタンのイベントハンドラーを設定
   */
  setupMenuButton() {
    const menuButton = document.getElementById('menu-button');
    if (menuButton) {
      menuButton.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (!this.isMenuEnabled(this.getUIFlags())) return;
        this.openSettings();
      });
    }
  }

  /**
   * 設定タイルボタンのイベントハンドラーを設定
   */
  setupTileButtons() {
    const tiles = [
      { id: 'tile-mode', view: 'mode' },
      { id: 'tile-skill', view: 'skill' },
      { id: 'tile-records', view: 'records' },
      { id: 'tile-settings', view: 'settings' },
    ];

    tiles.forEach(({ id, view }) => {
      const element = document.getElementById(id);
      if (element) {
        element.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.navigateTo(view);
        });
      }
    });
  }

  /**
   * 委譲されたイベントハンドラーを設定
   */
  setupDelegatedHandlers() {
    // 戻るボタンの委譲ハンドラー
    document.addEventListener('click', (ev) => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;

      const backEl = target.closest && target.closest('#settings-back');
      if (backEl) {
        ev.stopPropagation();
        this.goBack();
      }
    });
  }
}

// シングルトンインスタンスをエクスポート
export const menuSystem = new MenuSystem();

let pendingBackgroundMenuOpen = false;
let unsubscribeStatusWatcher = null;

function isDrawerCurrentlyOpen() {
  return document.documentElement.classList.contains(DOM_CONSTANTS.DRAWER_OPEN_CLASS);
}

function tryOpenBackgroundMenu() {
  if (!menuSystem.isInitialized) {
    return false;
  }

  const wasOpen = isDrawerCurrentlyOpen();
  const isOpen = menuSystem.openSettings();
  return wasOpen || Boolean(isOpen);
}

function ensurePendingBackgroundMenuOpen() {
  if (!pendingBackgroundMenuOpen) return;
  if (tryOpenBackgroundMenu()) {
    pendingBackgroundMenuOpen = false;
  }
}

visibilityManager.onBackgrounded(() => {
  const currentStatus = gameState.getState(STATE_PATHS.CORE_STATUS);

  // ゲーム進行中（Ready/Processing）のみポーズ＆メニューオープン
  // Title/GameOver/Pausedの場合は何もしない
  const shouldPause = currentStatus === GameStatus.Ready || currentStatus === GameStatus.Processing;

  if (shouldPause) {
    if (shouldBlockMenuOpen()) {
      pendingBackgroundMenuOpen = false;
      return;
    }

    pauseGame();

    if (!tryOpenBackgroundMenu()) {
      pendingBackgroundMenuOpen = true;
    } else {
      pendingBackgroundMenuOpen = false;
    }
  }
});

visibilityManager.onResumed(() => {
  ensurePendingBackgroundMenuOpen();
});

function watchStatusForMenuReadiness() {
  if (unsubscribeStatusWatcher) return;

  unsubscribeStatusWatcher = gameState.subscribe((event) => {
    if (event.path === STATE_PATHS.CORE_STATUS) {
      const nextStatus = event.value;

      const isMenuEligible = nextStatus === GameStatus.Ready || nextStatus === GameStatus.Title;
      if (isMenuEligible) {
        ensurePendingBackgroundMenuOpen();
      }

      const isDrawerOpen = gameState.getState(STATE_PATHS.UI_IS_DRAWER_OPEN);
      if (isDrawerOpen && nextStatus !== GameStatus.Paused) {
        pauseGame();
      }

      return;
    }

    if (event.path === STATE_PATHS.UI_IS_DRAWER_OPEN && pendingBackgroundMenuOpen) {
      ensurePendingBackgroundMenuOpen();
    }
  });
}

watchStatusForMenuReadiness();

visibilityManager.onResumed(() => {
  ensurePendingBackgroundMenuOpen();
});
